import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, WhatsappLogo, EnvelopeSimple, SignOut, ArrowClockwise, CircleNotch } from "@phosphor-icons/react";

const api = axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    withCredentials: true,
});

const STATUS_STYLES = {
    new: "border-blaze text-blaze",
    contacted: "border-gold text-gold",
    closed: "border-white/30 text-white/50",
};

function formatDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

async function callWithRefresh(fn) {
    try {
        return await fn();
    } catch (e) {
        if (e.response?.status === 401) {
            await api.post("/auth/refresh");
            return await fn();
        }
        throw e;
    }
}

export default function Admin() {
    const [user, setUser] = useState(undefined);
    const [enquiries, setEnquiries] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({ email: "", password: "" });
    const [submitting, setSubmitting] = useState(false);
    const [filter, setFilter] = useState("all");

    const fetchEnquiries = useCallback(async () => {
        setLoadingList(true);
        try {
            const res = await callWithRefresh(() => api.get("/enquiries"));
            setEnquiries(res.data);
        } catch (e) {
            setError(formatDetail(e.response?.data?.detail));
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await callWithRefresh(() => api.get("/auth/me"));
                setUser(res.data);
                fetchEnquiries();
            } catch {
                setUser(null);
            }
        })();
    }, [fetchEnquiries]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            const res = await api.post("/auth/login", form);
            setUser(res.data);
            fetchEnquiries();
        } catch (err) {
            setError(formatDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setUser(null);
    };

    const updateStatus = async (id, status) => {
        try {
            const res = await callWithRefresh(() => api.patch(`/enquiries/${id}`, { status }));
            setEnquiries((prev) => prev.map((en) => (en.id === id ? res.data : en)));
        } catch (e) {
            setError(formatDetail(e.response?.data?.detail));
        }
    };

    if (user === undefined) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-void" data-testid="admin-loading">
                <CircleNotch size={32} className="animate-spin text-blaze" />
            </div>
        );
    }

    if (user === null) {
        return (
            <div className="grid-backdrop flex min-h-screen items-center justify-center bg-void px-5">
                <motion.form
                    onSubmit={handleLogin}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-md border border-white/15 bg-panel p-8 md:p-10"
                    data-testid="admin-login-form"
                >
                    <p className="mb-2 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-blaze">
                        <span className="inline-block h-2 w-2 bg-blaze" />
                        Akash Network
                    </p>
                    <h1 className="mb-8 font-display text-4xl uppercase tracking-tight text-white">Admin Access</h1>
                    <label className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-white/50" htmlFor="admin-email">Email</label>
                    <input id="admin-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="industrial-input mb-6" data-testid="admin-email-input" />
                    <label className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-white/50" htmlFor="admin-password">Password</label>
                    <input id="admin-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="industrial-input mb-6" data-testid="admin-password-input" />
                    {error && <p className="mb-6 border border-blaze/50 bg-blaze/10 px-4 py-3 text-sm text-blaze" data-testid="admin-login-error">{error}</p>}
                    <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-3 bg-blaze px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-200 hover:bg-white hover:text-void disabled:opacity-60" data-testid="admin-login-submit">
                        {submitting ? <CircleNotch size={18} className="animate-spin" /> : "Sign In"}
                    </button>
                    <a href="/" className="mt-6 block text-center text-[11px] uppercase tracking-[0.2em] text-white/40 transition-colors duration-200 hover:text-blaze" data-testid="admin-back-home">Back to website</a>
                </motion.form>
            </div>
        );
    }

    const counts = {
        all: enquiries.length,
        new: enquiries.filter((e) => (e.status || "new") === "new").length,
        contacted: enquiries.filter((e) => e.status === "contacted").length,
        closed: enquiries.filter((e) => e.status === "closed").length,
    };
    const visible = filter === "all" ? enquiries : enquiries.filter((e) => (e.status || "new") === filter);

    return (
        <div className="min-h-screen bg-void px-5 py-8 md:px-10" data-testid="admin-dashboard">
            <header className="mb-10 flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
                <div>
                    <p className="mb-1 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-blaze">
                        <span className="inline-block h-2 w-2 bg-blaze" />
                        Akash Network
                    </p>
                    <h1 className="font-display text-3xl uppercase tracking-tight text-white md:text-4xl">Enquiries</h1>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={fetchEnquiries} className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70 transition-colors duration-200 hover:border-blaze hover:text-blaze" data-testid="admin-refresh-button">
                        <ArrowClockwise size={14} weight="bold" /> Refresh
                    </button>
                    <button onClick={handleLogout} className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70 transition-colors duration-200 hover:border-blaze hover:text-blaze" data-testid="admin-logout-button">
                        <SignOut size={14} weight="bold" /> Logout
                    </button>
                </div>
            </header>

            <div className="mb-8 flex flex-wrap gap-2" data-testid="admin-filters">
                {["all", "new", "contacted", "closed"].map((s) => (
                    <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}
                        className={`border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-200 ${filter === s ? "border-blaze bg-blaze text-white" : "border-white/20 text-white/60 hover:border-blaze hover:text-blaze"}`}>
                        {s} ({counts[s]})
                    </button>
                ))}
            </div>

            {error && <p className="mb-6 border border-blaze/50 bg-blaze/10 px-4 py-3 text-sm text-blaze" data-testid="admin-list-error">{error}</p>}

            {loadingList ? (
                <div className="flex justify-center py-20"><CircleNotch size={28} className="animate-spin text-blaze" /></div>
            ) : visible.length === 0 ? (
                <p className="border border-white/10 py-16 text-center text-sm uppercase tracking-[0.2em] text-white/40" data-testid="admin-empty-state">No enquiries here yet</p>
            ) : (
                <div className="space-y-4" data-testid="admin-enquiry-list">
                    {visible.map((en) => {
                        const status = en.status || "new";
                        return (
                            <article key={en.id} className="grid gap-6 border border-white/10 bg-panel p-6 md:grid-cols-12 md:p-8" data-testid={`enquiry-card-${en.id}`}>
                                <div className="md:col-span-4">
                                    <span className={`inline-block border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${STATUS_STYLES[status]}`} data-testid={`enquiry-status-${en.id}`}>{status}</span>
                                    <h2 className="mt-3 font-display text-2xl uppercase tracking-tight text-white">{en.name}</h2>
                                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-white/40">{new Date(en.created_at).toLocaleString("en-IN")}</p>
                                </div>
                                <div className="md:col-span-4">
                                    <p className="mb-1 text-[10px] uppercase tracking-[0.25em] text-blaze">{en.service}</p>
                                    <p className="text-sm leading-relaxed text-white/65">{en.message || "No message"}</p>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <a href={`tel:${en.phone}`} className="flex items-center gap-2 border border-white/15 px-3 py-1.5 text-xs text-white/70 transition-colors duration-200 hover:border-blaze hover:text-blaze" data-testid={`enquiry-call-${en.id}`}><Phone size={13} />{en.phone}</a>
                                        <a href={`https://wa.me/${en.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/15 px-3 py-1.5 text-xs text-white/70 transition-colors duration-200 hover:border-[#25D366] hover:text-[#25D366]" data-testid={`enquiry-whatsapp-${en.id}`}><WhatsappLogo size={13} />WhatsApp</a>
                                        {en.email && <a href={`mailto:${en.email}`} className="flex items-center gap-2 border border-white/15 px-3 py-1.5 text-xs text-white/70 transition-colors duration-200 hover:border-blaze hover:text-blaze" data-testid={`enquiry-email-${en.id}`}><EnvelopeSimple size={13} />{en.email}</a>}
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-start gap-2 md:col-span-4 md:justify-end">
                                    {["new", "contacted", "closed"].map((s) => (
                                        <button key={s} onClick={() => updateStatus(en.id, s)} disabled={status === s} data-testid={`enquiry-mark-${s}-${en.id}`}
                                            className={`border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-200 ${status === s ? "cursor-default border-blaze bg-blaze text-white" : "border-white/20 text-white/60 hover:border-blaze hover:text-blaze"}`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
